import { useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { getSessionCode } from '@/lib/session';

export default function FormSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      setTimeout(() => setStatus('idle'), 4000);
      return;
    }

    setStatus('loading');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d95s3c57gtqne2as9n00', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();

        const sessionCode = getSessionCode();

        supabase
          .from('sh_satis_leadleri')
          .insert({
            firma_adi: (formData.get('firma_adi') as string || '').trim(),
            yetkili_kisi: (formData.get('ad_soyad') as string || '').trim(),
            telefon: (formData.get('telefon') as string || '').trim(),
            instagram: (formData.get('instagram') as string || '').trim(),
            facebook: (formData.get('facebook') as string || '').trim(),
            sektor: (formData.get('sektor') as string || '').trim(),
            hizmet_urun: (formData.get('ana_hizmetler') as string || '').trim(),
            gunluk_butce: (formData.get('reklam_butcesi') as string || '').trim(),
            aylik_hedef: (formData.get('ciro_hedefi') as string || '').trim(),
            notlar: [
              'Hedef kitle: ' + ((formData.get('hedef_kitle') as string) || 'Belirtilmedi'),
              'Önceki ajans: ' + ((formData.get('onceki_ajans') as string) || 'Belirtilmedi'),
              'Zorluk: ' + ((formData.get('zorluk') as string) || 'Belirtilmedi'),
              'Ek notlar: ' + ((formData.get('ek_notlar') as string) || 'Belirtilmedi'),
            ].join(' | '),
            kaynak: 'landing_page',
            kaynak_durum: 'form_dolduruldu',
            lead_durumu: 'basvuru_yapti',
            lead_skoru: 50,
            funnel_tamamlandi: false,
            aktif: true,
            oturum_kodu: sessionCode,
          })
          .then(({ error }) => {
            if (error) {
              console.error('Supabase insert error:', error.message);
            }
          });
      } else {
        const serverMsg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          responseText ||
          'Bir hata oluştu. Lütfen tekrar deneyin.';
        setFormError(
          typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')
            ? 'Gönderiminiz alınamadı. Lütfen tekrar deneyin.'
            : serverMsg
        );
        setStatus('error');
      }
    } catch {
      setFormError('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.');
      setStatus('error');
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background-50" id="form">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
            Ücretsiz Örnek Sistem İçin
            <br />
            Firma Bilgilerinizi Gönderin
          </h2>
          <p className="text-foreground-600 text-sm">
            Bilgilerinizi gönderin, size özel hazırlanan ücretsiz örnek sistemi test edin.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="rounded-2xl bg-background-100 border border-background-200/50 p-6 md:p-8 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="ad_soyad" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Ad Soyad <span className="text-accent-600">*</span>
              </label>
              <input
                id="ad_soyad"
                name="ad_soyad"
                type="text"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="Adınız Soyadınız"
              />
            </div>
            <div>
              <label htmlFor="firma_adi" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Firma Adı <span className="text-accent-600">*</span>
              </label>
              <input
                id="firma_adi"
                name="firma_adi"
                type="text"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="Firmanızın adı"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="telefon" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Telefon / WhatsApp <span className="text-accent-600">*</span>
              </label>
              <input
                id="telefon"
                name="telefon"
                type="tel"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="05XX XXX XX XX"
              />
            </div>
            <div>
              <label htmlFor="instagram" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Instagram Sayfa Adresi
              </label>
              <input
                id="instagram"
                name="instagram"
                type="text"
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="@kullaniciadi veya link"
              />
            </div>
          </div>

          <div>
            <label htmlFor="facebook" className="block text-foreground-700 text-sm font-medium mb-1.5">
              Varsa Facebook Sayfa Adresi
            </label>
            <input
              id="facebook"
              name="facebook"
              type="text"
              className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
              placeholder="Facebook sayfa linki (varsa)"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sektor" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Sektörünüz <span className="text-accent-600">*</span>
              </label>
              <select
                id="sektor"
                name="sektor"
                required
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all appearance-none cursor-pointer"
                defaultValue=""
              >
                <option value="" disabled>Seçiniz</option>
                <option value="guzellik">Güzellik / Bakım</option>
                <option value="egitim">Eğitim</option>
                <option value="saglik">Sağlık / Klinik</option>
                <option value="gayrimenkul">Gayrimenkul</option>
                <option value="servis">Servis / Teknik Hizmet</option>
                <option value="yerel-hizmet">Yerel Hizmet</option>
                <option value="diger">Diğer</option>
              </select>
            </div>
            <div>
              <label htmlFor="ana_hizmetler" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Verdiğiniz Ana Hizmetler
              </label>
              <input
                id="ana_hizmetler"
                name="ana_hizmetler"
                type="text"
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="Ana hizmet alanlarınız"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="hedef_kitle" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Hedef Kitleniz
              </label>
              <input
                id="hedef_kitle"
                name="hedef_kitle"
                type="text"
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="Hedef kitlenizi kısaca tanımlayın"
              />
            </div>
            <div>
              <label htmlFor="ciro_hedefi" className="block text-foreground-700 text-sm font-medium mb-1.5">
                Aylık Ciro / Kazanç Hedefiniz
              </label>
              <input
                id="ciro_hedefi"
                name="ciro_hedefi"
                type="text"
                className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
                placeholder="Opsiyonel"
              />
            </div>
          </div>

          <div>
            <label htmlFor="reklam_butcesi" className="block text-foreground-700 text-sm font-medium mb-1.5">
              Günlük / Aylık Reklam Bütçesi Düşünceniz
            </label>
            <input
              id="reklam_butcesi"
              name="reklam_butcesi"
              type="text"
              className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all"
              placeholder="Örn: Günlük 200 TL / Aylık 6.000 TL"
            />
          </div>

          <div>
            <label htmlFor="onceki_ajans" className="block text-foreground-700 text-sm font-medium mb-1.5">
              Daha Önce Reklam Ajansıyla Çalıştınız mı?
            </label>
            <select
              id="onceki_ajans"
              name="onceki_ajans"
              className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all appearance-none cursor-pointer"
              defaultValue=""
            >
              <option value="" disabled>Seçiniz</option>
              <option value="evet-memnun">Evet, memnun kaldım</option>
              <option value="evet-memnun-degil">Evet, memnun kalmadım</option>
              <option value="hayir">Hayır, çalışmadım</option>
            </select>
          </div>

          <div>
            <label htmlFor="zorluk" className="block text-foreground-700 text-sm font-medium mb-1.5">
              En Çok Zorlandığınız Konu Nedir?
            </label>
            <textarea
              id="zorluk"
              name="zorluk"
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all resize-none"
              placeholder="Reklam, müşteri takibi, satış süreci gibi konularda en çok nerede zorlanıyorsunuz?"
            ></textarea>
            <p className="text-foreground-400 text-xs mt-1">En fazla 500 karakter</p>
          </div>

          <div>
            <label htmlFor="ek_notlar" className="block text-foreground-700 text-sm font-medium mb-1.5">
              Eklemek İstedikleriniz
            </label>
            <textarea
              id="ek_notlar"
              name="ek_notlar"
              rows={2}
              maxLength={500}
              className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300/60 text-foreground-800 text-sm focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-all resize-none"
              placeholder="Varsa eklemek istediğiniz notlar..."
            ></textarea>
          </div>

          <input
            type="text"
            name="phone_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="form-honeypot"
          />

          <div>
            {status === 'success' && (
              <div className="rounded-lg bg-accent-100/70 border border-accent-200/50 p-4 mb-4">
                <p className="text-foreground-800 text-sm font-medium">
                  Talebiniz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.
                </p>
              </div>
            )}
            {status === 'error' && formError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4 mb-4">
                <p className="text-red-700 text-sm">{formError}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {status === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Gönderiliyor...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  Ücretsiz Örnek Sistem Talep Et
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}